import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type MovieAutoplayMode, getMovieAutoplayMode, setMovieAutoplayMode } from '../../../utils/movieAutoplay';
import { type ContentDataSource, getContentDataSource, setContentDataSource } from '../../../utils/contentDataSource';
import {
  getLiveBufferMinutes,
  LIVE_BUFFER_MINUTES_KEY,
  LIVE_BUFFER_MINUTES_OPTIONS,
  type LiveBufferMinutes,
} from '../../../utils/liveBuffer';

export type LiveEnginePreference = 'auto' | 'ts' | 'hls' | 'native-hls';
export const LIVE_ENGINE_KEY = 'ott_live_engine';

export function getLiveEnginePreference(): LiveEnginePreference {
  const stored = localStorage.getItem(LIVE_ENGINE_KEY);
  if (stored === 'ts' || stored === 'hls' || stored === 'native-hls') return stored;
  return 'auto';
}

interface OtherSettingsSectionProps {
  focused: boolean;
}

const OtherSettingsSection: React.FC<OtherSettingsSectionProps> = ({ focused }) => {
  const { t } = useTranslation();

  const [liveEngine, setLiveEngine] = React.useState<LiveEnginePreference>(getLiveEnginePreference);
  const [liveBufferMinutes, setLiveBufferMinutes] = React.useState<LiveBufferMinutes>(getLiveBufferMinutes);
  const [movieAutoplay, setMovieAutoplay] = React.useState<MovieAutoplayMode>(getMovieAutoplayMode);
  const [contentSource, setContentSource] = React.useState<ContentDataSource>(getContentDataSource);
  const firstBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) {
      firstBtnRef.current?.focus();
    }
  }, [focused]);

  const handleEngineSelect = (engine: LiveEnginePreference) => {
    setLiveEngine(engine);
    localStorage.setItem(LIVE_ENGINE_KEY, engine);
  };

  const handleBufferSelect = (minutes: LiveBufferMinutes) => {
    setLiveBufferMinutes(minutes);
    localStorage.setItem(LIVE_BUFFER_MINUTES_KEY, String(minutes));
  };

  const handleAutoplaySelect = (mode: MovieAutoplayMode) => {
    setMovieAutoplay(mode);
    setMovieAutoplayMode(mode);
  };

  const handleContentSourceSelect = (source: ContentDataSource) => {
    setContentSource(source);
    setContentDataSource(source);
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section__title">{t('settings.other.title')}</h2>

      <div className="settings-card">
        <div className="settings-row__label" style={{ marginBottom: 8 }}>
          {t('settings.other.liveEngine')}
        </div>

        {(['auto', 'hls', 'native-hls', 'ts'] as LiveEnginePreference[]).map((opt, idx) => {
          const isSelected = liveEngine === opt;
          const labelKey = opt === 'auto' ? 'settings.other.liveEngineAuto'
            : opt === 'hls' ? 'settings.other.liveEngineHls'
            : opt === 'native-hls' ? 'settings.other.liveEngineNativeHls'
            : 'settings.other.liveEngineTs';
          return (
            <div
              key={opt}
              ref={idx === 0 ? firstBtnRef : undefined}
              className={['settings-radio-row', isSelected ? 'settings-radio-row--selected' : ''].filter(Boolean).join(' ')}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => handleEngineSelect(opt)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleEngineSelect(opt);
                }
              }}
            >
              <div className={`settings-radio-dot${isSelected ? ' settings-radio-dot--selected' : ''}`}>
                {isSelected && <div className="settings-radio-dot__inner" />}
              </div>
              <div className="settings-radio-label">
                <p className="settings-radio-label__title">{t(labelKey)}</p>
              </div>
            </div>
          );
        })}

        <div className="settings-row__label" style={{ margin: '18px 0 8px' }}>
          {t('settings.other.liveBuffer')}
        </div>
        <div className="settings-row__description" style={{ marginBottom: 12, opacity: 0.7, fontSize: 13 }}>
          {t('settings.other.liveBufferDesc')}
        </div>

        {LIVE_BUFFER_MINUTES_OPTIONS.map((minutes) => {
          const isSelected = liveBufferMinutes === minutes;
          return (
            <div
              key={minutes}
              className={['settings-radio-row', isSelected ? 'settings-radio-row--selected' : ''].filter(Boolean).join(' ')}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => handleBufferSelect(minutes)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleBufferSelect(minutes);
                }
              }}
            >
              <div className={`settings-radio-dot${isSelected ? ' settings-radio-dot--selected' : ''}`}>
                {isSelected && <div className="settings-radio-dot__inner" />}
              </div>
              <div className="settings-radio-label">
                <p className="settings-radio-label__title">{t('common.minutes', { count: minutes })}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="settings-card">
        <div className="settings-row__label" style={{ marginBottom: 8 }}>
          {t('settings.other.movieAutoplay')}
        </div>
        <div className="settings-row__description" style={{ marginBottom: 12, opacity: 0.7, fontSize: 13 }}>
          {t('settings.other.movieAutoplayDesc')}
        </div>

        {(['trailer', 'movie'] as MovieAutoplayMode[]).map((opt) => {
          const isSelected = movieAutoplay === opt;
          const titleKey = opt === 'trailer' ? 'settings.other.movieAutoplayTrailer'
            : 'settings.other.movieAutoplayMovie';
          const descKey = opt === 'trailer' ? 'settings.other.movieAutoplayTrailerDesc'
            : 'settings.other.movieAutoplayMovieDesc';
          return (
            <div
              key={opt}
              className={['settings-radio-row', isSelected ? 'settings-radio-row--selected' : ''].filter(Boolean).join(' ')}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => handleAutoplaySelect(opt)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleAutoplaySelect(opt);
                }
              }}
            >
              <div className={`settings-radio-dot${isSelected ? ' settings-radio-dot--selected' : ''}`}>
                {isSelected && <div className="settings-radio-dot__inner" />}
              </div>
              <div className="settings-radio-label">
                <p className="settings-radio-label__title">{t(titleKey)}</p>
                <p className="settings-radio-label__desc">{t(descKey)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="settings-card">
        <div className="settings-row__label" style={{ marginBottom: 8 }}>
          {t('settings.other.contentSource')}
        </div>
        <div className="settings-row__description" style={{ marginBottom: 12, opacity: 0.7, fontSize: 13 }}>
          {t('settings.other.contentSourceDesc')}
        </div>

        {(['tmdb', 'xtream'] as ContentDataSource[]).map((opt) => {
          const isSelected = contentSource === opt;
          const titleKey = opt === 'tmdb' ? 'settings.other.contentSourceTmdb' : 'settings.other.contentSourceXtream';
          const descKey  = opt === 'tmdb' ? 'settings.other.contentSourceTmdbDesc' : 'settings.other.contentSourceXtreamDesc';
          return (
            <div
              key={opt}
              className={['settings-radio-row', isSelected ? 'settings-radio-row--selected' : ''].filter(Boolean).join(' ')}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => handleContentSourceSelect(opt)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleContentSourceSelect(opt);
                }
              }}
            >
              <div className={`settings-radio-dot${isSelected ? ' settings-radio-dot--selected' : ''}`}>
                {isSelected && <div className="settings-radio-dot__inner" />}
              </div>
              <div className="settings-radio-label">
                <p className="settings-radio-label__title">{t(titleKey)}</p>
                <p className="settings-radio-label__desc">{t(descKey)}</p>
              </div>
            </div>
          );
        })}

        {contentSource === 'xtream' && (
          <div className="settings-warning-row" style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(245,166,35,0.12)', border: '1px solid rgba(245,166,35,0.4)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: '#f5a623', fontSize: 14, lineHeight: 1, marginTop: 1 }}>⚠</span>
            <span style={{ color: '#f5a623', fontSize: 13, lineHeight: 1.4 }}>
              {t('settings.other.contentSourceXtreamWarning')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtherSettingsSection;
